import { useQuery, useMutation, useQueryClient, type Query } from '@tanstack/react-query'
import { estimatesApi, type EstimateDeferralAction } from './estimates'
import { queryKeys } from './query-keys'
import type { ApiEstimate, EstimateStatus, MarkTotalLossRequest } from './types'

type RefetchInterval =
  | number
  | false
  | ((query: Query<ApiEstimate>) => number | false | undefined)

/** Fetch all estimates for a single team. Shared across sidebar, estimates page, reports, etc. */
export function useEstimates(teamId: string | number | null) {
  return useQuery({
    queryKey: queryKeys.estimates.list(teamId!),
    queryFn: () => estimatesApi.list(teamId!),
    enabled: !!teamId,
  })
}

/** Fetch a single estimate by ID, with optional auto-polling.
 *  `refetchInterval` accepts a number, false, or a function that receives
 *  the current Query so callers can poll only while data is in a transient
 *  state (e.g. AI processing). */
export function useEstimate(
  estimateId: string | number | null,
  options?: { refetchInterval?: RefetchInterval },
) {
  return useQuery({
    queryKey: queryKeys.estimates.detail(estimateId!),
    queryFn: () => estimatesApi.get(estimateId!),
    enabled: !!estimateId,
    refetchInterval: options?.refetchInterval,
  })
}

/** Fetch estimates across all dealerships in a group (parallel fetch, flattened). */
export function useGroupEstimates(
  dealerships: { id: string }[],
  enabled: boolean,
) {
  const key = dealerships.map(d => d.id).sort().join(',')
  return useQuery({
    queryKey: [...queryKeys.estimates.all, 'group', key],
    queryFn: () =>
      Promise.all(dealerships.map(d => estimatesApi.list(d.id))).then(r =>
        r.flat(),
      ),
    enabled: enabled && dealerships.length > 0,
  })
}

/** Mutate estimate status with optimistic cache update. Invalidates all estimate queries on settle. */
export function useUpdateEstimateStatus() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({
      estimateId,
      status,
    }: {
      estimateId: string | number
      status: Exclude<EstimateStatus, 'Loading'>
    }) => estimatesApi.updateStatus(estimateId, { status }),

    onMutate: async ({ estimateId, status }) => {
      await qc.cancelQueries({ queryKey: queryKeys.estimates.all })

      // Snapshot every cache under ['estimates'] — both list-shaped
      // (ApiEstimate[]) and detail-shaped (ApiEstimate). One predicate,
      // both shapes captured for rollback.
      const previousCaches = qc.getQueriesData<ApiEstimate[] | ApiEstimate>({
        queryKey: queryKeys.estimates.all,
      })
      const now = new Date().toISOString()

      qc.setQueriesData<ApiEstimate[] | ApiEstimate>(
        { queryKey: queryKeys.estimates.all },
        old => {
          if (!old) return old
          if (Array.isArray(old)) {
            return old.map(est =>
              String(est.id) === String(estimateId)
                ? { ...est, status, updatedAt: now }
                : est,
            )
          }
          // Detail cache — single object. Update if it's the targeted one;
          // leave neighbouring detail caches alone.
          return String(old.id) === String(estimateId)
            ? { ...old, status, updatedAt: now }
            : old
        },
      )

      return { previousCaches }
    },

    onError: (_err, _vars, context) => {
      context?.previousCaches.forEach(([key, data]) => {
        qc.setQueryData(key, data)
      })
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
    },
  })
}

/** PATCH /v1/estimates/:id/customer — add/fix the customer's phone or email so
 *  automated follow-up can run. MOCK-ONLY endpoint (real BE has none). Invalidate
 *  estimates AND the customer-activity feed (the drip re-derives once a number
 *  exists) so the no-phone banner clears without a manual refresh. */
export function useUpdateEstimateCustomer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      estimateId,
      phone,
      email,
    }: {
      estimateId: number | string
      phone?: string
      email?: string
    }) => estimatesApi.updateCustomer(estimateId, { phone, email }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
      qc.invalidateQueries({ queryKey: queryKeys.customerActivity.all })
    },
  })
}

/** Helper: invalidate all estimate queries (call after creating a new estimate, etc.) */
export function useInvalidateEstimates() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
}

/** POST /v1/estimates/:id/total-loss — bodyshop estimator/GM only on the BE.
 *  Side effect: a SalesLead is created on the dealership side; invalidate
 *  both the estimate cache (so `totalLoss=true` shows up) and the salesLeads
 *  cache (so the dealership /total-loss page sees the new pending lead). */
export function useMarkAsTotalLoss() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ estimateId, data }: { estimateId: number | string; data?: MarkTotalLossRequest }) =>
      estimatesApi.markAsTotalLoss(estimateId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
      qc.invalidateQueries({ queryKey: queryKeys.salesLeads.all })
    },
  })
}

/** PATCH /v1/estimates/:id/deferral — mock-only deferral marker.
 *  `confirm_dead` also flips status → Declined and records an activity
 *  event for the estimate's creator, so the activity cache is invalidated
 *  alongside estimates. */
export function useSetDeferral() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      estimateId,
      action,
      note,
    }: {
      estimateId: number | string
      action: EstimateDeferralAction
      note?: string
    }) => estimatesApi.setDeferral(estimateId, action, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
      qc.invalidateQueries({ queryKey: queryKeys.activity.all })
    },
  })
}

/** DELETE /v1/estimates/:id/total-loss — clears the flag and removes the SalesLead. */
export function useUnmarkAsTotalLoss() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (estimateId: number | string) => estimatesApi.unmarkTotalLoss(estimateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
      qc.invalidateQueries({ queryKey: queryKeys.salesLeads.all })
    },
  })
}
